import type { StateCreator } from 'zustand';
import type { ItineraryState } from '../itinerary-store';
import { defaultLayerPrefs, type LayerId } from '../../lib/layer-registry';
import { defaultReviewLayerPrefs, type ReviewLayerId } from '../../lib/review-layer-registry';

/**
 * UI layer preferences — drives `useLayer(layerId)` (Plan) and
 * `useReviewLayer(layerId)` (Review).
 *
 * Stored once per user (not per trip): a layer turned off applies
 * everywhere the user goes. Persisted via the existing Zustand persist
 * middleware (see `partialize` in `itinerary-store.ts`).
 *
 * `layerPrefs` carries the effective per-layer value the UI reads via
 * `s.layerPrefs[id] ?? defaultOn`. `layerPrefsExplicit` carries the
 * subset of layer ids the user has DIRECTLY toggled — only those ids
 * end up on the server. Layers absent from the explicit set fall
 * through to the registry default at read time, so a later default
 * change reaches the user automatically (ADR-0082 supersedes the
 * persistence shape from ADR-0040).
 *
 * The two are deliberately separate maps — a user who wants minimal
 * Plan overlays might still want a rich Review summary, and vice versa.
 */

// `Partial` matches the persisted-state reality: a layer added to the
// registry after a user last saved is missing from their stored blob,
// and the access-site `?? defaultOn` fallback paints in the new entry's
// default. Without the `Partial`, TypeScript treats every read as
// non-nullable and flags those fallbacks as dead.
export type LayerPrefs = Partial<Record<LayerId, boolean>>;
export type ReviewLayerPrefs = Partial<Record<ReviewLayerId, boolean>>;

export const DEFAULT_LAYER_PREFS: LayerPrefs = defaultLayerPrefs();
export const DEFAULT_REVIEW_LAYER_PREFS: ReviewLayerPrefs = defaultReviewLayerPrefs();

export interface LayersSlice {
  layerPrefs: LayerPrefs;
  /** Layer ids the user has explicitly toggled. Subset of the keys in
   *  `layerPrefs`. The sync layer ships ONLY these ids to the server
   *  (see ADR-0082). Order is insertion-order; ordering doesn't carry
   *  semantic meaning so consumers MUST treat this as a set. */
  layerPrefsExplicit: LayerId[];
  reviewLayerPrefs: ReviewLayerPrefs;
  reviewLayerPrefsExplicit: ReviewLayerId[];
  setLayerVisible: (layerId: LayerId, value: boolean) => void;
  /** Restore every Plan layer to its registry default. Clears the
   *  explicit set so the next flush ships an empty record and the
   *  server stops pinning prior choices. */
  resetLayers: () => void;
}

/** Insert `id` into `arr` if it's not already there. Order-preserving;
 *  callers don't depend on ordering but keeping it stable avoids
 *  needless Zustand subscriber rerenders. */
function addExplicit<T extends string>(arr: T[], id: T): T[] {
  return arr.includes(id) ? arr : [...arr, id];
}

export const createLayersSlice: StateCreator<ItineraryState, [], [], LayersSlice> = (set, get) => ({
  layerPrefs: DEFAULT_LAYER_PREFS,
  layerPrefsExplicit: [],
  reviewLayerPrefs: DEFAULT_REVIEW_LAYER_PREFS,
  reviewLayerPrefsExplicit: [],

  setLayerVisible: (layerId, value) => {
    set((s) => ({
      layerPrefs: { ...s.layerPrefs, [layerId]: value },
      layerPrefsExplicit: addExplicit(s.layerPrefsExplicit, layerId),
    }));
    get().markUserPrefsDirty();
  },

  resetLayers: () => {
    // Both the values map AND the explicit set reset — without
    // clearing explicit, the next flush would ship `{explicit:[...],
    // values:{...defaults}}` and re-pin the registry defaults on the
    // server, defeating ADR-0082.
    set({ layerPrefs: defaultLayerPrefs(), layerPrefsExplicit: [] });
    get().markUserPrefsDirty();
  },
});
